import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import orderService from '../../db/order-service';
import { jsPDF } from 'jspdf';
import { formatPrice, getUserCountry } from '../../utils/currency';
import './CustomerOrderList.scss';

const CustomerOrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userCountry, setUserCountry] = useState('IN');
  const customerId = sessionStorage.getItem('customerId');
  const navigate = useNavigate();

  useEffect(() => {
    const country = getUserCountry();
    setUserCountry(country);
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!customerId) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        const customerOrders = await orderService.findOrdersByCustomerId(customerId);
        setOrders(customerOrders);
        setError(null);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load your orders. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [customerId, navigate]);

  // Helper function to get expected delivery date
  const getDeliveryDate = (order) => {
    const orderDate = new Date(order.date);
    const deliveryDate = new Date(orderDate);
    deliveryDate.setDate(orderDate.getDate() + 5); // Assuming 5 days for delivery
    return deliveryDate.toLocaleDateString();
  };

  const viewInvoice = (order) => {
    // Create a new window for the invoice
    const invoiceWindow = window.open('', '_blank');
    invoiceWindow.document.write('<html><head><title>Invoice</title>');
    invoiceWindow.document.write('<style>');
    invoiceWindow.document.write(`
      body { font-family: Arial, sans-serif; padding: 20px; }
      .invoice { max-width: 800px; margin: 0 auto; padding: 20px; border: 1px solid #ccc; }
      .invoice-header { display: flex; justify-content: space-between; margin-bottom: 20px; }
      .invoice-details { text-align: right; }
      .company-header { margin-bottom: 20px; }
      .company-header h1 { margin: 0; color: #133c0b; }
      .company-header p { margin: 5px 0; }
      table { width: 100%; border-collapse: collapse; margin: 20px 0; }
      th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
      .total-row td { font-weight: bold; }
      .customer-details, .order-details, .payment-details { margin-bottom: 20px; }
      .payment-details { background-color: #f8f8f8; padding: 15px; border-radius: 5px; border-left: 4px solid #4caf50; }
      .footer { margin-top: 30px; text-align: center; font-size: 14px; }
      .print-button { background-color: #133c0b; color: white; border: none; padding: 10px 20px; 
                      cursor: pointer; margin-top: 20px; display: block; margin: 20px auto; }
      @media print { .print-button { display: none; } }
    `);
    invoiceWindow.document.write('</style></head><body>');
    
    // Invoice content
    invoiceWindow.document.write('<div class="invoice">');
    
    // Company header
    invoiceWindow.document.write('<div class="company-header">');
    invoiceWindow.document.write('<h1>ORGANIC GREENS HUB</h1>');
    invoiceWindow.document.write('<p>Plot No. 142, First Floor, Raghavendra Nagar, Jillelaguda</p>');
    invoiceWindow.document.write('<p>Hyderabad – 500097, Telangana, India</p>');
    invoiceWindow.document.write('<p>Phone: +91 9491657949</p>');
    invoiceWindow.document.write('</div>');
    
    // Invoice header
    invoiceWindow.document.write('<div class="invoice-header">');
    invoiceWindow.document.write('<div>');
    invoiceWindow.document.write('<h2>COMMERCIAL INVOICE</h2>');
    invoiceWindow.document.write('</div>');
    invoiceWindow.document.write('<div class="invoice-details">');
    invoiceWindow.document.write(`<p><strong>Invoice No:</strong> INV-${order._id.substring(0, 8).toUpperCase()}</p>`);
    invoiceWindow.document.write(`<p><strong>Date:</strong> ${new Date(order.date).toLocaleDateString()}</p>`);
    invoiceWindow.document.write(`<p><strong>Order ID:</strong> ${order._id}</p>`);
    invoiceWindow.document.write(`<p><strong>Payment Status:</strong> ${order.paymentStatus || order.status}</p>`);
    invoiceWindow.document.write(`<p><strong>Delivery Status:</strong> ${order.deliveryStatus || 'Pending'}</p>`);
    invoiceWindow.document.write('</div>');
    invoiceWindow.document.write('</div>');
    
    // Customer details
    const customerName = sessionStorage.getItem('customerName') || 'Customer';
    const customerEmail = sessionStorage.getItem('customerEmail') || '';
    
    invoiceWindow.document.write('<div class="customer-details">');
    invoiceWindow.document.write('<h3>Customer Information</h3>');
    invoiceWindow.document.write(`<p><strong>Name:</strong> ${customerName}</p>`);
    invoiceWindow.document.write(`<p><strong>Email:</strong> ${customerEmail}</p>`);
    invoiceWindow.document.write(`<p><strong>Address:</strong> ${order.shippingAddress || 'Not provided'}</p>`);
    if (order.deliveryDate) {
      invoiceWindow.document.write(`<p><strong>Delivery Date:</strong> ${order.deliveryDate}</p>`);
    }
    if (order.deliveryTimeSlot) {
      invoiceWindow.document.write(`<p><strong>Delivery Time:</strong> ${order.deliveryTimeSlot}</p>`);
    }
    invoiceWindow.document.write('</div>');
    
    // Payment details section (if status is Paid)
    if (order.status === 'paid' || order.paymentStatus === 'Paid') {
      invoiceWindow.document.write('<div class="payment-details" style="margin-bottom: 20px;">');
      invoiceWindow.document.write('<h3>Payment Information</h3>');
      invoiceWindow.document.write(`<p><strong>Payment Status:</strong> Paid</p>`);
      if (order.paymentId) {
        invoiceWindow.document.write(`<p><strong>Payment ID:</strong> ${order.paymentId}</p>`);
      }
      invoiceWindow.document.write(`<p><strong>Payment Date:</strong> ${new Date(order.updatedAt || order.date).toLocaleString()}</p>`);
      invoiceWindow.document.write('</div>');
    }
    
    // Order details
    invoiceWindow.document.write('<div class="order-details">');
    invoiceWindow.document.write('<h3>Order Details</h3>');
    invoiceWindow.document.write('<table>');
    invoiceWindow.document.write('<thead><tr><th>Item</th><th>Quantity</th><th>Unit Price</th><th>Total</th></tr></thead>');
    invoiceWindow.document.write('<tbody>');
    
    order.items.forEach(item => {
      invoiceWindow.document.write('<tr>');
      invoiceWindow.document.write(`<td>${item.name}</td>`);
      invoiceWindow.document.write(`<td>${item.quantity}</td>`);
      invoiceWindow.document.write(`<td>${formatPrice(item.price, userCountry)}</td>`);
      invoiceWindow.document.write(`<td>${formatPrice(item.price * item.quantity, userCountry)}</td>`);
      invoiceWindow.document.write('</tr>');
    });
    
    invoiceWindow.document.write('</tbody>');
    invoiceWindow.document.write('<tfoot>');
    invoiceWindow.document.write('<tr class="total-row">');
    invoiceWindow.document.write('<td colspan="3" style="text-align: right;">Total:</td>');
    invoiceWindow.document.write(`<td>${formatPrice(order.totalAmount, userCountry)}</td>`);
    invoiceWindow.document.write('</tr>');
    invoiceWindow.document.write('</tfoot>');
    invoiceWindow.document.write('</table>');
    invoiceWindow.document.write('</div>');
    
    // Footer
    invoiceWindow.document.write('<div class="footer">');
    invoiceWindow.document.write('<p>Thank you for shopping with Organic Greens Hub!</p>');
    invoiceWindow.document.write('<p>For any questions, please contact support@organicgreenshub.com</p>');
    invoiceWindow.document.write('</div>');
    
    // Print button
    invoiceWindow.document.write('<button class="print-button" onclick="window.print()">Print Invoice</button>');
    
    invoiceWindow.document.write('</div>');
    invoiceWindow.document.write('</body></html>');
    invoiceWindow.document.close();
  };

  const generateInvoicePDF = (order) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Add company logo (base64 encoded placeholder - replace with actual logo)
    const logoBase64 = 'data:image/png;base64,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';
    doc.addImage(logoBase64, 'PNG', 20, 10, 40, 20);
    
    // Add company header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('ORGANIC GREENS HUB', 70, 15);
    
    // Add company details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Plot No. 142, First Floor, Raghavendra Nagar, Jillelaguda', 70, 22);
    doc.text('Hyderabad – 500097, Telangana, India', 70, 27);
    doc.text('Phone: +91 9491657949', 70, 32);
    
    // Add document title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('COMMERCIAL INVOICE', pageWidth / 2, 45, { align: 'center' });
    
    // Add horizontal line
    doc.setLineWidth(0.5);
    doc.line(20, 50, pageWidth - 20, 50);
    
    // Add invoice details section
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice No:', 20, 60);
    doc.text('Invoice Date:', 20, 65);
    doc.text('Order ID:', 20, 70);
    doc.text('Payment Status:', 20, 75);
    doc.text('Delivery Status:', 20, 80);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`INV-${order._id.substring(0, 8).toUpperCase()}`, 70, 60);
    doc.text(new Date(order.date).toLocaleDateString(), 70, 65);
    doc.text(order._id, 70, 70);
    doc.text(order.paymentStatus || order.status || 'Pending', 70, 75);
    doc.text(order.deliveryStatus || 'Pending', 70, 80);
    
    // Add consignee details section
    doc.setFont('helvetica', 'bold');
    doc.text('CONSIGNEE:', 20, 95);
    
    doc.setFont('helvetica', 'normal');
    const customerName = sessionStorage.getItem('customerName') || 'Customer';
    const customerEmail = sessionStorage.getItem('customerEmail') || '';
    doc.text(`Name: ${customerName}`, 20, 100);
    doc.text(`Customer ID: ${order.customerId}`, 20, 105);
    doc.text(`Email: ${customerEmail}`, 20, 110);
    doc.text(`Address: ${order.shippingAddress || 'Not provided'}`, 20, 115);
    
    // Add shipment details section
    doc.setFont('helvetica', 'bold');
    doc.text('SHIPMENT DETAILS:', pageWidth - 100, 95);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Shipping Method: Express Delivery`, pageWidth - 100, 100);
    doc.text(`Expected Delivery: ${getDeliveryDate(order)}`, pageWidth - 100, 105);
    doc.text(`Origin: Hyderabad, India`, pageWidth - 100, 110);
    doc.text(`Destination: ${order.shippingAddress ? order.shippingAddress.split(',').pop().trim() : 'Not provided'}`, pageWidth - 100, 115);
    
    // Add items table
    doc.setFont('helvetica', 'bold');
    doc.text('ORDER DETAILS:', 20, 130);
    
    // Table header
    let y = 140;
    doc.setFillColor(240, 240, 240);
    doc.rect(20, y - 5, pageWidth - 40, 8, 'F');
    doc.text('Product Description', 25, y);
    doc.text('Quantity', 100, y);
    doc.text('Unit Price', 130, y);
    doc.text('Amount', 170, y);
    
    // Table content
    y += 10;
    doc.setFont('helvetica', 'normal');
    
    order.items.forEach(item => {
      doc.text(item.name, 25, y);
      doc.text(item.quantity.toString(), 100, y);
      doc.text(formatPrice(item.price, userCountry), 130, y);
      doc.text(formatPrice(item.price * item.quantity, userCountry), 170, y);
      y += 8;
    });
    
    // Add total section
    y += 5;
    doc.line(20, y - 3, pageWidth - 20, y - 3);
    doc.setFont('helvetica', 'bold');
    doc.text('Subtotal:', 130, y + 5);
    doc.text('Shipping:', 130, y + 13);
    doc.text('Total:', 130, y + 21);
    
    doc.setFont('helvetica', 'normal');
    const subtotal = order.totalAmount;
    const shipping = 0; // Assuming free shipping
    doc.text(formatPrice(subtotal, userCountry), 170, y + 5);
    doc.text(formatPrice(shipping, userCountry), 170, y + 13);
    doc.setFont('helvetica', 'bold');
    doc.text(formatPrice(subtotal + shipping, userCountry), 170, y + 21);
    
    // Add terms and conditions
    y += 40;
    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions:', 20, y);
    doc.setFont('helvetica', 'normal');
    doc.text('1. All items are certified organic and meet export standards.', 20, y + 8);
    doc.text('2. Payment is due as per the terms agreed upon at the time of order.', 20, y + 16);
    doc.text('3. Claims for damaged goods must be made within 24 hours of receipt.', 20, y + 24);
    doc.text('4. Returns accepted within 7 days with prior authorization.', 20, y + 32);
    doc.text('5. Organic Greens Hub reserves the right to update or modify these Terms and Conditions at any time without prior notice. We encourage customers to review these terms before placing each order.', 20, y + 32);
    doc.text('6. All final decisions regarding orders, disputes, and policies are at the sole discretion of the Organic Greens Hub Authority.', 20, y + 32);
    doc.text('7. While we strive to source the highest quality inputs, please note that the majority of our ingredients are organic, though select items may vary based on availability.', 20, y + 32);
    // Add signature section
    y += 50;
    doc.line(20, y, 80, y);
    doc.line(pageWidth - 80, y, pageWidth - 20, y);
    doc.text('Authorized Signature', 30, y + 5);
    doc.text('Customer Signature', pageWidth - 70, y + 5);
    
    // Add footer
    doc.setFontSize(8);
    doc.text('This is a computer-generated invoice and does not require a physical signature.', pageWidth / 2, pageHeight - 20, { align: 'center' });
    doc.text('Organic Greens Hub - Bringing Nature\'s Best to Your Doorstep', pageWidth / 2, pageHeight - 15, { align: 'center' });
    
    // Save the PDF
    doc.save(`commercial-invoice-${order._id}.pdf`);
  };

  if (loading) {
    return <div className="customer-orders-container">Loading your orders...</div>;
  }

  if (error) {
    return (
      <div className="customer-orders-container">
        <div className="error-message">{error}</div>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="customer-orders-container">
      <h1>My Past Orders</h1>
      
      {orders.length === 0 ? (
        <div className="no-orders">
          <p>You haven't placed any orders yet.</p>
          <button onClick={() => navigate('/')}>Browse Products</button>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map(order => (
            <div key={order._id} className="order-card">
              <div className="order-header">
                <div className="order-id">Order #{order._id}</div>
                <div className="order-date">{new Date(order.date).toLocaleDateString()}</div>
              </div>
              
              <div className="order-items">
                {order.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="item-name">{item.name}</div>
                    <div className="item-quantity">Qty: {item.quantity}</div>
                    <div className="item-price">{formatPrice(item.price * item.quantity, userCountry)}</div>
                  </div>
                ))}
              </div>
              
              <div className="order-footer">
                <div className="order-total">Total: {formatPrice(order.totalAmount, userCountry)}</div>
                <div className="order-status">
                  <div>Payment Status: {order.paymentStatus || order.status}</div>
                  <div>Delivery Status: {order.deliveryStatus || 'Pending'}</div>
                  {order.status === 'deferred' && order.deliveryStatus !== 'Refund Complete' && (
                    <div className="refund-notice">
                      ⚠️ Order deferred - Items unavailable. Refund will be processed in 5 business days.
                    </div>
                  )}
                  {order.deliveryStatus === 'Refund Complete' && (
                    <div className="refund-complete-notice">
                      ✓ Refund completed on {new Date(order.refundCompletedDate).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div className="order-actions">
                  {(() => {
                    const orderDate = new Date(order.date);
                    const today = new Date();
                    const isSameDay = orderDate.toDateString() === today.toDateString();
                    const canCancel = isSameDay && order.status !== 'cancelled' && order.status !== 'deferred' && order.deliveryStatus !== 'Delivered';
                    
                    return canCancel ? (
                      <button 
                        className="cancel-order"
                        onClick={async () => {
                          if (window.confirm('Are you sure you want to cancel this order?')) {
                            try {
                              await orderService.updateOrderStatus(order._id, 'cancelled');
                              window.location.reload();
                            } catch (err) {
                              alert('Failed to cancel order. Please try again.');
                            }
                          }
                        }}
                      >
                        Cancel Order
                      </button>
                    ) : null;
                  })()}
                </div>
                <div className="invoice-buttons">
                  <button 
                    className="view-invoice" 
                    onClick={() => viewInvoice(order)}
                  >
                    View Invoice
                  </button>
                  <button 
                    className="download-invoice" 
                    onClick={() => generateInvoicePDF(order)}
                  >
                    Download Invoice
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerOrderList;